import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Bell, CreditCard, Users, Vote, Car, FileText, 
  AlertTriangle, Check, CheckCheck, Trash2, X, UserPlus, Wifi, WifiOff
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';

const notificationIcons = {
  payment_created: CreditCard,
  payment_received: CreditCard,
  payment_reminder: CreditCard,
  payment_overdue: AlertTriangle,
  member_joined: Users,
  member_left: Users,
  vote_created: Vote,
  vote_ended: Vote,
  withdrawal_request: AlertTriangle,
  withdrawal_approved: Check,
  withdrawal_rejected: X,
  carpool_created: Car,
  carpool_request: Car,
  carpool_accepted: Car,
  file_uploaded: FileText,
  announcement: Bell,
  system: Bell,
  reminder_payment: CreditCard,
  reminder_vote: Vote,
  reminder_custom: Bell,
  join_request: UserPlus,
  join_approved: Check,
  join_rejected: X,
};

const notificationColors = {
  payment_overdue: 'text-red-500',
  withdrawal_request: 'text-orange-500',
  withdrawal_rejected: 'text-red-500',
  withdrawal_approved: 'text-green-500',
  payment_received: 'text-green-500',
  join_approved: 'text-green-500',
  join_rejected: 'text-red-500',
  default: 'text-slate-500 dark:text-slate-400',
};

// Category translations
const categoryLabels = {
  'pt-BR': {
    payment: 'Pagamento',
    member: 'Membro',
    vote: 'Votação',
    reminder: 'Lembrete',
    carpool: 'Carona',
    withdrawal: 'Desistência',
    file: 'Arquivo',
    general: 'Geral',
  },
  'en-US': {
    payment: 'Payment',
    member: 'Member',
    vote: 'Vote',
    reminder: 'Reminder',
    carpool: 'Carpool',
    withdrawal: 'Withdrawal',
    file: 'File',
    general: 'General',
  }
};

export const NotificationBell = () => {
  const { api, isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  
  // Get realtime context for SSE connection status and updates
  const { 
    isConnected: sseConnected, 
    unreadCount: realtimeUnreadCount,
    recentNotifications: realtimeNotifications
  } = useRealtime();
  
  const dateLocale = language === 'pt-BR' ? ptBR : enUS;
  const labels = categoryLabels[language] || categoryLabels['pt-BR'];

  // Get notification category based on type
  const getCategory = (type) => {
    if (!type) return labels.general;
    
    if (type.includes('payment') || type === 'reminder_payment') {
      return labels.payment;
    }
    if (type.includes('member') || type === 'join_request' || type === 'join_approved' || type === 'join_rejected') {
      return labels.member;
    }
    if (type.includes('vote') || type === 'reminder_vote') {
      return labels.vote;
    }
    if (type.includes('reminder') || type.includes('task')) {
      return labels.reminder;
    }
    if (type.includes('carpool')) {
      return labels.carpool;
    }
    if (type.includes('withdrawal')) {
      return labels.withdrawal;
    }
    if (type.includes('file')) {
      return labels.file;
    }
    return labels.general;
  };

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.get('/notifications/summary');
      setNotifications(response.data.recent || []);
      setUnreadCount(response.data.unread || 0);
    } catch (error) {
      console.log('Error fetching notifications:', error);
    }
  }, [api, isAuthenticated]);

  // Initial fetch
  useEffect(() => {
    if (isAuthenticated) {
      // Use queueMicrotask to defer state update
      let isMounted = true;
      queueMicrotask(() => {
        if (isMounted) {
          fetchNotifications();
        }
      });
      return () => { isMounted = false; };
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling (slower when SSE is connected)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Poll less frequently when SSE is connected (60s), more frequently when disconnected (15s)
    const pollInterval = sseConnected ? 60000 : 15000;
    const interval = setInterval(fetchNotifications, pollInterval);
    return () => clearInterval(interval);
  }, [sseConnected, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update when realtime notifications arrive
  useEffect(() => {
    if (realtimeUnreadCount > 0) {
      let isMounted = true;
      const updateNotifications = async () => {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (isMounted) {
          fetchNotifications();
        }
      };
      updateNotifications();
      return () => { isMounted = false; };
    }
  }, [realtimeUnreadCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.log('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.log('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }
    
    // Navigate based on type
    if (notification.trip_id) {
      const type = notification.type || '';
      
      if (type.includes('payment') || type === 'reminder_payment') {
        navigate(`/trip/${notification.trip_id}/payments`);
      } else if (type === 'reminder_vote' || (type.includes('vote') && !type.includes('reminder'))) {
        navigate(`/trip/${notification.trip_id}/votes`);
      } else if (type.includes('withdrawal')) {
        navigate(`/trip/${notification.trip_id}/members`);
      } else if (type.includes('carpool')) {
        navigate(`/trip/${notification.trip_id}/carpools`);
      } else if (type.includes('file')) {
        navigate(`/trip/${notification.trip_id}/files`);
      } else if (type === 'join_approved') {
        navigate(`/trip/${notification.trip_id}`);
      } else if (type === 'join_request' || type === 'join_rejected') {
        navigate(`/trip/${notification.trip_id}/members`);
      } else if (type.includes('reminder') || type.includes('task')) {
        navigate(`/trip/${notification.trip_id}/tasks`);
      } else {
        navigate(`/trip/${notification.trip_id}`);
      }
    }
    
    setOpen(false);
  };

  const getIcon = (type) => {
    const Icon = notificationIcons[type] || Bell;
    const color = notificationColors[type] || notificationColors.default;
    return <Icon className={`w-4 h-4 ${color}`} />;
  };

  const formatTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: dateLocale });
    } catch {
      return '';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {/* SSE connection indicator - small dot */}
          <span 
            className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white dark:border-slate-800 ${
              sseConnected ? 'bg-green-500' : 'bg-yellow-500'
            }`}
            title={sseConnected ? 'Tempo real ativo' : 'Reconectando...'}
          />
        </Button>
      </DropdownMenuTrigger>
      {/* Mobile: full width, Desktop: fixed width */}
      <DropdownMenuContent 
        align="end" 
        className="w-[calc(100vw-32px)] sm:w-80 max-w-[400px] mx-4 sm:mx-0"
        sideOffset={8}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900 dark:text-white">Notificações</h3>
            {sseConnected ? (
              <Wifi className="w-3 h-3 text-green-500" title="Tempo real ativo" />
            ) : (
              <WifiOff className="w-3 h-3 text-yellow-500" title="Reconectando..." />
            )}
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs h-7 text-slate-600 dark:text-slate-300"
              onClick={handleMarkAllAsRead}
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Marcar todas
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-slate-500 dark:text-slate-400">
              <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300 dark:text-slate-600 dark:text-slate-400" />
              <p className="text-sm">Nenhuma notificação</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`px-3 py-2 border-b border-slate-100 dark:border-slate-700 last:border-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${
                  !notification.read ? 'bg-orange-50/50 dark:bg-orange-900/20' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex gap-3">
                  <div className="mt-0.5">
                    {getIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wide">
                      {language === 'pt-BR' ? 'Notificação' : 'Notification'}: {getCategory(notification.type)}
                    </span>
                    <p className={`text-sm ${!notification.read ? 'font-medium' : ''} text-slate-900 dark:text-white truncate`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2" />
                  )}
                </div>
              </div>
            ))
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator className="bg-slate-100 dark:bg-slate-700" />
            <div className="p-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300"
                onClick={() => {
                  navigate('/notifications');
                  setOpen(false);
                }}
              >
                Ver todas as notificações
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
